import { RateType, useRepositoryInfiniteQuery, useRepositorySuspenseQuery } from '@mews-commander/data-access';
import { useCurrentEnterpriseId } from '@mews-commander/core';
import { isAdditionalService, isBookableService } from '@mews-commander/legacy-entities/src/ducks/servicesNew';
import { sortOrderableEntity } from '@mews-commander/legacy-entities/src/utils/orderableEntities';

export const useServicesSuspenseQuery = () => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    return useRepositorySuspenseQuery(
        'services',
        'getAll',
        { enterpriseIds: [currentEnterpriseId] },
        {
            select: (data) => ({
                bookableServices: data.services.filter(isBookableService).sort(sortOrderableEntity),
                additionalServices: data.services.filter(isAdditionalService).sort(sortOrderableEntity),
            }),
        }
    );
};

export const useRatesSuspenseQuery = () => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    return useRepositorySuspenseQuery(
        'rates',
        'getAll',
        { enterpriseIds: [currentEnterpriseId] },
        {
            select: (data) => ({
                ...data,
                rates: data.rates?.filter((rate) => rate.type !== RateType.AvailabilityBlock),
            }),
        }
    );
};

export const useProductsSuspenseQuery = () => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    return useRepositorySuspenseQuery('products', 'getAll', {
        enterpriseIds: [currentEnterpriseId],
        extent: { baseProducts: true },
    });
};

export const useProductCategoriesSuspenseQuery = () => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    return useRepositorySuspenseQuery('productCategories', 'getAll', {
        enterpriseIds: [currentEnterpriseId],
    });
};

export const companiesCountPerPage = 100;

interface UseCompaniesSuspenseQueryProps {
    searchToken: string;
    enterpriseId: string;
    enabled: boolean;
}

export const useCompaniesSuspenseQuery = ({ searchToken, enterpriseId, enabled }: UseCompaniesSuspenseQueryProps) => {
    const { data: services } = useRepositorySuspenseQuery('services', 'getAll', { enterpriseIds: [enterpriseId] });

    return useRepositoryInfiniteQuery(
        'companies',
        'search',
        ({ pageParam }) => ({ searchToken, enterpriseId, startIndex: pageParam, count: companiesCountPerPage }),
        {
            initialPageParam: 0,
            getNextPageParam: (lastPage, allPages) =>
                lastPage.companies?.length != null && lastPage.companies.length < companiesCountPerPage
                    ? undefined
                    : allPages.length * companiesCountPerPage,
            select: (data) => {
                const companies = data.pages.flatMap((page) => page.companies ?? []);
                const addresses = data.pages.flatMap((page) => page.addresses ?? []);
                const travelAgencyContracts = data.pages
                    .flatMap((page) => page.travelAgencyContracts ?? [])
                    .filter((travelAgency) =>
                        services.services.some((service) => service.id === travelAgency.serviceId)
                    );
                return companies.map((company) => ({
                    ...company,
                    address: addresses.find(
                        // @ts-expect-error $TSFixMeError: Property id does not exist on type {}
                        (address) => address.owner?.value.id != null && address.owner?.value.id === company.id
                    ),
                    travelAgencyContract: travelAgencyContracts.find((contract) => contract?.companyId === company.id),
                }));
            },
            enabled,
        }
    );
};

export const useAccountingCategoriesSuspenseQuery = () => {
    const currentEnterpriseId = useCurrentEnterpriseId();
    return useRepositorySuspenseQuery(
        'accountingCategories',
        'getAll',
        {
            enterpriseIds: [currentEnterpriseId],
        },
        {
            select: (data) => data?.accountingCategories?.filter((category) => category.isActive),
        }
    );
};
